import React from 'react';

// ── Badge ─────────────────────────────────────────────────────────────────────
const BADGE_VARIANTS = {
  green:  { bg: 'rgba(40,120,60,0.22)',   color: '#6eca7c', border: 'rgba(110,202,124,0.35)' },
  amber:  { bg: 'rgba(230,172,0,0.15)',    color: 'var(--gold-bright)', border: 'rgba(230,172,0,0.45)'  },
  red:    { bg: 'rgba(160,30,30,0.22)',   color: '#e07878', border: 'rgba(224,120,120,0.38)' },
  blue:   { bg: 'rgba(30,70,160,0.22)',   color: '#80b0f8', border: 'rgba(128,176,248,0.38)' },
  purple: { bg: 'rgba(90,30,160,0.22)',   color: '#b880f8', border: 'rgba(184,128,248,0.38)' },
  teal:   { bg: 'rgba(10,90,90,0.22)',    color: '#50c8c8', border: 'rgba(80,200,200,0.38)'  },
  gray:   { bg: 'rgba(255,255,255,0.07)', color: '#aaaaaa', border: 'rgba(255,255,255,0.15)' },
  gold:   { bg: 'rgba(230,172,0,0.15)',   color: 'var(--gold-bright)', border: 'rgba(230,172,0,0.45)'  },
};

export function Badge({ variant = 'gray', children }) {
  const s = BADGE_VARIANTS[variant] || BADGE_VARIANTS.gray;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', display: 'inline-block', letterSpacing: '0.02em' }}>
      {children}
    </span>
  );
}

export function statusBadge(status) {
  const map = {
    confirmed:  <Badge variant="green">Confirmed</Badge>,
    paid:       <Badge variant="green">Paid</Badge>,
    pending:    <Badge variant="amber">Pending</Badge>,
    overdue:    <Badge variant="red">Overdue</Badge>,
    incomplete: <Badge variant="amber">Incomplete</Badge>,
    waitlisted: <Badge variant="blue">Waitlisted</Badge>,
    partial:    <Badge variant="purple">Partial</Badge>,
    open:       <Badge variant="green">Open</Badge>,
    upcoming:   <Badge variant="blue">Upcoming</Badge>,
    closed:     <Badge variant="gray">Closed</Badge>,
    invoiced:   <Badge variant="blue">Invoiced</Badge>,
    manual:     <Badge variant="teal">Manual</Badge>,
    stripe:     <Badge variant="blue">Stripe</Badge>,
  };
  return map[status] || <Badge>{status}</Badge>;
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ variant = 'primary', size = 'md', children, style, ...props }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontFamily: 'var(--font-sans)', fontWeight: 700, cursor: 'pointer',
    border: 'none', borderRadius: 'var(--radius-md)', transition: 'all 0.18s',
    whiteSpace: 'nowrap', letterSpacing: '0.01em',
  };
  const sizes = {
    sm: { padding: '5px 12px', fontSize: 12 },
    md: { padding: '8px 18px', fontSize: 13 },
    lg: { padding: '11px 24px', fontSize: 14 },
  };
  const variants = {
    primary: { background: 'var(--gold-main)', color: '#050505', boxShadow: 'none' },
    secondary: { background: 'var(--surface-1)', color: 'var(--text-primary)', border: '1px solid var(--border-dark)' },
    dark: { background: '#111111', color: 'var(--gold-main)', border: '1px solid var(--gold-border)' },
    danger: { background: 'rgba(160,30,30,0.1)', color: '#c0392b', border: '1px solid rgba(160,30,30,0.3)' },
    ghost: { background: 'transparent', color: 'var(--text-muted)' },
  };
  return (
    <button style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      onMouseEnter={e => { if (variant==='primary') { e.currentTarget.style.background='var(--gold-bright)'; } else if (variant==='secondary') e.currentTarget.style.background='var(--surface-2)'; }}
      onMouseLeave={e => { if (variant==='primary') { e.currentTarget.style.background='var(--gold-main)'; } else if (variant==='secondary') e.currentTarget.style.background='var(--surface-1)'; }}
      {...props}>
      {children}
    </button>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style, padding = '1.25rem', gold, ...props }) {
  return (
    <div style={{ background: 'var(--surface-0)', border: gold ? '1px solid var(--gold-border)' : '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding, boxShadow: '0 10px 28px rgba(0,0,0,0.32)', ...style }} {...props}>
      {children}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, subColor, icon }) {
  return (
    <div style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', position: 'relative', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--gold-main)' }} />
      {icon && <div style={{ marginBottom: 8, color: 'var(--gold-main)', display: 'flex' }}>{icon}</div>}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: '#ffffff', fontFamily: 'var(--font-condensed)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: subColor || 'var(--gold-main)', marginTop: 5, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 540 }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface-0)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.45)', border: '1px solid var(--gold-border)' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--gold-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#111111' }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: 'var(--gold-main)', fontFamily: 'var(--font-condensed)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--gold-border)', cursor: 'pointer', color: 'var(--gold-main)', fontSize: 18, lineHeight: 1, padding: '2px 8px', borderRadius: 4 }}>×</button>
        </div>
        <div style={{ padding: '1.5rem' }}>{children}</div>
      </div>
    </div>
  );
}

// ── FormField ─────────────────────────────────────────────────────────────────
export function FormField({ label, required, error, children, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}{required && <span style={{ color: '#c62828', marginLeft: 2 }}>*</span>}</label>}
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: '#c62828' }}>{error}</span>}
    </div>
  );
}

export function Input({ style, ...props }) {
  return <input style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius-md)', fontSize: 14, color: 'var(--text-primary)', background: 'var(--surface-1)', outline: 'none', transition: 'border-color 0.15s', ...style }}
    onFocus={e => e.target.style.borderColor = 'var(--gold-main)'}
    onBlur={e => e.target.style.borderColor = 'var(--border-dark)'}
    {...props} />;
}

export function Select({ style, children, ...props }) {
  return <select style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius-md)', fontSize: 14, color: 'var(--text-primary)', background: 'var(--surface-1)', outline: 'none', ...style }} {...props}>{children}</select>;
}

export function Textarea({ style, ...props }) {
  return <textarea style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius-md)', fontSize: 14, color: 'var(--text-primary)', background: 'var(--surface-1)', outline: 'none', resize: 'vertical', fontFamily: 'var(--font-sans)', ...style }} {...props} />;
}

// ── Page Header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 900, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)', letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-block', width: 3, height: 22, background: 'linear-gradient(to bottom, var(--gold-bright), var(--gold-deep))', borderRadius: 2, flexShrink: 0 }} />
          {title}
        </h1>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3, marginLeft: 13 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ── GoldDivider ───────────────────────────────────────────────────────────────
export function GoldDivider() {
  return <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, var(--gold-border), transparent)', margin: '1rem 0' }} />;
}

// ── Inline Editable (admin) ───────────────────────────────────────────────────
export function EditableText({ value, onSave, tag: Tag = 'span', style, isAdmin }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value);
  const inputRef = React.useRef(null);
  React.useEffect(() => { setVal(value); }, [value]);
  React.useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  if (!isAdmin) return <Tag style={style}>{value}</Tag>;
  if (editing) {
    return (
      <input ref={inputRef} value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => { onSave && onSave(val); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { onSave && onSave(val); setEditing(false); } if (e.key === 'Escape') { setVal(value); setEditing(false); } }}
        style={{ ...style, border: '1.5px solid var(--gold-main)', borderRadius: 4, padding: '2px 6px', outline: 'none', background: 'var(--gold-pale)', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }} />
    );
  }
  return (
    <Tag onClick={() => setEditing(true)} title="Click to edit"
      style={{ ...style, cursor: 'pointer', textDecoration: 'underline dashed var(--gold-muted)', textUnderlineOffset: 3 }}>
      {val}
    </Tag>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n || 0);
export const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export const FORMAT_LABELS = {
  pool_single:        'Pool play → Single elim',
  single_elimination: 'Single elimination',
  double_elimination: 'Double elimination',
  round_robin:        'Round robin',
};
